import { config } from '@common/config';
import { OptionsViewModel, PageViewModel } from '@models/common.models';

export interface AutoAuthoViewModel {
  readonly authorizationLimit?: number;
  readonly emailAddress?: string;
  readonly swoIsCheckAndAdvise?: boolean;
  readonly swoIsEligible?: boolean;
  readonly swoTypeIsEligible?: boolean;
  readonly tripStatus?: number;
  readonly vendorIsEligibleForAutoAutho?: boolean;
  readonly vendorIsEligibleForStatusUpdate?: boolean;
}

export interface ConfirmationRequestViewModel {
  readonly authorizationAmount?: number;
  readonly hasNonCoveredCosts?: boolean;
  readonly isPossibleDenial?: boolean;
  readonly needsEquipmentOrdered?: boolean;
}

export interface ConfirmationViewModel {
  readonly authorizationAmountExceedsLimit?: boolean;
  readonly authorizationCode?: string;
  readonly hasNonCoveredCosts?: boolean;
  readonly isConfirmed?: boolean;
  readonly isPossibleDenial?: boolean;
  readonly needsEquipmentOrdered?: boolean;
}

export interface SetTripStatusRequestViewModel {
  readonly callbackWaitDays?: number;
  readonly comment?: string;
  readonly estimatedCompletionDate?: string;
  readonly followupReason?: number;
  readonly timeZone: string;
  readonly tripDateFrom?: string;
  readonly tripDateTo?: string;
  readonly tripStatus: number;
}

/**
 * Service to interact with API that provides SWO/Vendor information.
 * This service does not inherit from BaseService for the following reasons:
 *   1) AutoAutho API (and its associated pages in this site) uses an alternate authentication mechanism
 */
class AutoAuthoService {
  private readonly baseUri: string;

  private static createAuthorizationHeader(phoneNumber: string, swoId: number): string {
    const authorizationValue = phoneNumber.concat(':', swoId.toString());
    return `Basic ${btoa(authorizationValue)}`;
  }

  constructor(baseUri: string) {
    if (baseUri.lastIndexOf('/') === baseUri.length - 1) {
      baseUri = baseUri.substr(0, baseUri.length - 1);
    }
    this.baseUri = baseUri;
  }

  public async confirmAutoAutho(
    phoneNumber: string,
    swoId: number,
    request: ConfirmationRequestViewModel
  ): Promise<ConfirmationViewModel> {
    const relativeUri: string = 'autoautho/confirm';
    const uri: string = this.baseUri.concat('/', relativeUri);
    const response: Response = await fetch(uri, {
      body: JSON.stringify(request),
      headers: {
        authorization: AutoAuthoService.createAuthorizationHeader(phoneNumber, swoId),
        'content-type': 'application/json'
      },
      method: 'POST'
    });
    if (!response.ok) {
      throw new Error(
        `Encountered error communicating with CIL API, status code: "${
          response.status
        }", body: "${await response.json()}".`
      );
    }

    return (await response.json()) as ConfirmationViewModel;
  }

  public async getAutoAuthoViewModel(phoneNumber: string, swoId: number): Promise<AutoAuthoViewModel | null> {
    const relativeUri: string = 'autoautho/vendor';
    const uri: string = this.baseUri.concat('/', relativeUri);
    const response: Response = await fetch(uri, {
      headers: {
        authorization: AutoAuthoService.createAuthorizationHeader(phoneNumber, swoId)
      },
      method: 'GET'
    });
    if (response.status === 401) {
      return null;
    }
    if (!response.ok) {
      throw new Error(
        `Encountered error communicating with CIL API, status code: "${
          response.status
        }", body: "${await response.json()}".`
      );
    }

    return (await response.json()) as AutoAuthoViewModel;
  }

  public async getSwoPortalStatus(phoneNumber: string, swoId: number, id: number): Promise<OptionsViewModel | null> {
    const relativeUri: string = `autoautho/swoPortalStatuses/${id}`;
    const uri: string = this.baseUri.concat('/', relativeUri);
    const response: Response = await fetch(uri, {
      headers: {
        authorization: AutoAuthoService.createAuthorizationHeader(phoneNumber, swoId)
      },
      method: 'GET'
    });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(
        `Encountered error communicating with CIL API, status code: "${
          response.status
        }", body: "${await response.json()}".`
      );
    }

    return (await response.json()) as OptionsViewModel;
  }

  public async getSwoPortalStatuses(phoneNumber: string, swoId: number): Promise<PageViewModel<OptionsViewModel>> {
    const relativeUri: string = 'autoautho/swoPortalStatuses?pageNumber=0&pageSize=1000';
    const uri: string = this.baseUri.concat('/', relativeUri);
    const response: Response = await fetch(uri, {
      headers: {
        authorization: AutoAuthoService.createAuthorizationHeader(phoneNumber, swoId)
      },
      method: 'GET'
    });
    if (!response.ok) {
      throw new Error(
        `Encountered error communicating with CIL API, status code: "${
          response.status
        }", body: "${await response.json()}".`
      );
    }

    return (await response.json()) as PageViewModel<OptionsViewModel>;
  }

  public async getSwoPortalTripReasons(phoneNumber: string, swoId: number): Promise<PageViewModel<OptionsViewModel>> {
    const relativeUri: string = 'autoautho/swoPortalTripReasons?pageNumber=0&pageSize=1000';
    const uri: string = this.baseUri.concat('/', relativeUri);
    const response: Response = await fetch(uri, {
      headers: {
        authorization: AutoAuthoService.createAuthorizationHeader(phoneNumber, swoId)
      },
      method: 'GET'
    });
    if (!response.ok) {
      throw new Error(
        `Encountered error communicating with CIL API, status code: "${
          response.status
        }", body: "${await response.json()}".`
      );
    }

    return (await response.json()) as PageViewModel<OptionsViewModel>;
  }

  public async setTripStatus(
    phoneNumber: string,
    swoId: number,
    request: SetTripStatusRequestViewModel
  ): Promise<void> {
    const uri = this.baseUri.concat('/autoautho/setTripStatus');
    const response: Response = await fetch(uri, {
      body: JSON.stringify(request),
      headers: {
        authorization: AutoAuthoService.createAuthorizationHeader(phoneNumber, swoId),
        'content-type': 'application/json'
      },
      method: 'POST'
    });
    if (!response.ok) {
      throw new Error(
        `Encountered error communicating with CIL API, status code: "${
          response.status
        }", body: "${await response.json()}".`
      );
    }
  }
}

export const autoAuthoService = new AutoAuthoService(config.apiHost);



// WEBPACK FOOTER //
// ./src/_services/AutoAuthoService.ts